import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CONDITIONS } from "@/data/conditions";
import { TESTIMONIALS } from "@/data/testimonials";
import { submitBooking } from "@/lib/bookingService";

const PHONE_1 = "9871053777";
const PHONE_2 = "9217221357";
const PHONE_TEL_1 = "+919871053777";
const PHONE_TEL_2 = "+919217221357";
const EMAIL = "info@physiosewa.in";
const TAGLINE = "Advanced Physiotherapy & Rehabilitation · Restoring Movement, Rebuilding Life";
const HOURS_FULL = "Mon–Sun: 9:00 AM – 9:00 PM";

// ===== WhatsApp links (all open in new tab) =====
const WA = {
  base:        "https://wa.me/919871053777",
  book:        "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20would%20like%20to%20book%20an%20appointment",
  enquiry:     "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20have%20an%20enquiry",
  freeConsult: "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20would%20like%20to%20book%20my%20free%20consultation",
  services:    "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20would%20like%20to%20enquire%20about%20physiotherapy%20services",
  contact:     "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20would%20like%20to%20get%20in%20touch",
  clinic1:     "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20am%20interested%20in%20a%20single%20clinic%20session%20at%20%E2%82%B9800",
  clinic7:     "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20am%20interested%20in%20the%207-day%20clinic%20package%20at%20%E2%82%B95%2C250",
  clinic30:    "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20am%20interested%20in%20the%2030-day%20clinic%20programme%20at%20%E2%82%B919%2C500",
  home1:       "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20am%20interested%20in%20a%20home%20visit%20session%20at%20%E2%82%B9950",
  home7:       "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20am%20interested%20in%20the%207-day%20home%20visit%20package%20at%20%E2%82%B96%2C300",
  home30:      "https://wa.me/919871053777?text=Hi%20PhysioSewa%2C%20I%20am%20interested%20in%20the%2030-day%20home%20visit%20programme%20at%20%E2%82%B924%2C000",
};

// Public link to Google Business Profile listing.
// TODO: replace with the exact Google Business Profile short URL for PhysioSewa once available.
const GBP_URL = "https://www.google.com/search?q=PhysioSewa+Sector+110+Gurugram+reviews";
const INSTAGRAM_URL = "https://www.instagram.com/physiosewa.in";

const NAV_LINKS = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "about", label: "About" },
  { id: "why", label: "Why Us" },
  { id: "testimonials", label: "Testimonials" },
  { id: "pricing", label: "Pricing" },
  { id: "contact", label: "Contact" },
];

// ==================== Offer Banner ====================
const OfferBanner = ({ visible, onDismiss }) => {
  if (!visible) return null;
  return (
    <div className="offer-banner" data-testid="offer-banner" role="region" aria-label="Special offer">
      <div className="offer-banner-inner">
        <span className="offer-banner-text offer-banner-full">
          First assessment free · Home visits across Dwarka Expressway ·{" "}
          <a href={`tel:${PHONE_TEL_1}`} data-testid="banner-phone-link">Call {PHONE_1}</a>
        </span>
        <span className="offer-banner-text offer-banner-short">
          First assessment free ·{" "}
          <a href={`tel:${PHONE_TEL_1}`} data-testid="banner-phone-link-short">Call {PHONE_1}</a>
        </span>
        <button
          onClick={onDismiss}
          className="offer-banner-close"
          aria-label="Dismiss offer banner"
          data-testid="banner-dismiss"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
    </div>
  );
};

// ==================== Logo ====================
const Logo = ({ light = false, withTagline = false }) => (
  <div className="flex items-center gap-2.5" data-testid="brand-logo">
    <img src="/assets/physiosewa-logo.png" alt="PhysioSewa physiotherapy clinic Gurugram logo" className="h-9 w-auto rounded-md" />
    <div className="flex flex-col leading-tight">
      <span style={{ fontWeight: 800, fontSize: "1.2rem", color: light ? "#FFFFFF" : "var(--text-dark)", letterSpacing: "-0.01em" }}>
        Physio<span style={{ color: "var(--primary)" }}>Sewa</span>
      </span>
      {withTagline && (
        <span
          className="brand-tagline"
          style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: light ? "rgba(255,255,255,0.7)" : "var(--text-mid)", marginTop: 1 }}
        >
          Care · Cure · Recover
        </span>
      )}
    </div>
  </div>
);

// ==================== Navbar ====================
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 80);
      const sections = NAV_LINKS.map(l => document.getElementById(l.id));
      const top = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = sections[i];
        if (el && el.offsetTop <= top) { setActive(NAV_LINKS[i].id); break; }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <>
      <nav className={`navbar ${scrolled ? "scrolled" : ""}`} data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between h-[56px] lg:h-[72px]">
          <button onClick={() => goTo("home")} className="bg-transparent border-0 cursor-pointer p-0">
            <Logo withTagline />
          </button>
          <div className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map(l => (
              <button
                key={l.id}
                onClick={() => goTo(l.id)}
                className={`nav-link ${active === l.id ? "active" : ""}`}
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <div className="hidden lg:flex items-center gap-5">
            <a href={`tel:${PHONE_TEL_1}`} className="text-sm font-semibold flex items-center gap-1.5"
               style={{ color: "var(--primary)" }} data-testid="nav-phone-link">
              <i className="fa-solid fa-phone"></i> {PHONE_1}
            </a>
            <a
              href={WA.freeConsult}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-pill"
              data-testid="nav-book-btn"
            >
              Book your free assessment
            </a>
          </div>
          <button
            className="lg:hidden bg-transparent border-0 p-2 -mr-2"
            style={{ color: "var(--text-dark)", fontSize: "1.6rem", lineHeight: 1 }}
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
          >
            <i className="fa-solid fa-bars"></i>
          </button>
        </div>
      </nav>

      <div className={`mobile-menu ${open ? "open" : ""}`} data-testid="mobile-menu">
        <button
          onClick={() => setOpen(false)}
          className="mobile-menu-close"
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <i className="fa-solid fa-xmark"></i>
        </button>
        <div className="mobile-menu-brand">
          <span style={{ fontWeight: 800, fontSize: "1.25rem", color: "#FFFFFF" }}>
            Physio<span style={{ color: "var(--primary-light)" }}>Sewa</span>
          </span>
        </div>
        <nav className="mobile-menu-links">
          {NAV_LINKS.map(l => (
            <button
              key={l.id}
              onClick={() => goTo(l.id)}
              className="mobile-menu-link"
              data-testid={`mobile-nav-${l.id}`}
            >
              {l.label}
              <i className="fa-solid fa-chevron-right" style={{ fontSize: "0.75rem", opacity: 0.6 }}></i>
            </button>
          ))}
        </nav>
        <div className="mobile-menu-cta">
          <a href={`tel:${PHONE_TEL_1}`} className="mobile-menu-phone" data-testid="mobile-menu-call">
            <i className="fa-solid fa-phone"></i> Call {PHONE_1} | {PHONE_2}
          </a>
          <a
            href={WA.freeConsult}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="mobile-menu-book"
            data-testid="mobile-menu-book"
          >
            Book your free assessment <i className="fa-solid fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </>
  );
};

// ==================== Hero ====================
const Hero = () => {
  return (
    <section id="home" className="hero-section-v2" data-testid="hero-section">
      <div className="hero-v2-grid">
        <div className="hero-v2-left" data-testid="hero-text-wrap">
          <span className="hero-badge" data-testid="hero-location-badge">
            <i className="fa-solid fa-location-dot"></i> Sector 110, Gurugram
          </span>
          <div className="hero-kicker mt-3" data-testid="hero-kicker">
            Care · Cure · Recover
          </div>
          <h1 className="hero-h1 hero-h1-v2 mt-2" data-testid="hero-headline">
            Move Freely Again{"\u00A0—"}{" "}
            <span style={{ color: "var(--primary)" }}>Physiotherapy in Sector 110, Gurugram</span>
          </h1>
          <p className="hero-sub hero-sub-v2 mt-4" data-testid="hero-subtext">
            At our clinic or at your home, across Dwarka Expressway. Assessed and treated by
            Dr. Anusha Dubey — the same physiotherapist, every session.
          </p>

          <div className="hero-cta-v2 mt-6">
            <a
              href={WA.freeConsult}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-pill hero-btn-primary"
              data-testid="hero-book-btn"
            >
              Book your free assessment <i className="fa-solid fa-arrow-right"></i>
            </a>
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="hero-tertiary-link"
              data-testid="hero-pricing-btn"
              type="button"
            >
              See pricing <i className="fa-solid fa-arrow-right"></i>
            </button>
          </div>

          <div className="hero-trust-line mt-6" data-testid="hero-trust-line">
            <a href={GBP_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-google-rating">
              <i className="fa-solid fa-star" style={{ color: "var(--gold)" }}></i> 5.0 ★ · 68 Google reviews
            </a>
            <span className="hero-trust-sep">·</span>
            <span>2000+ patients</span>
            <span className="hero-trust-sep">·</span>
            <span>Open 7 days</span>
            <span className="hero-trust-sep">·</span>
            <span>Home visits</span>
          </div>
        </div>

        <div className="hero-v2-right" data-testid="hero-visual">
          {/* Credential tiles — three-up row that fills the column meaningfully with no photo */}
          <ul className="hero-cred-tiles" data-testid="hero-cred-tiles">
            <li>
              <div className="cred-num">8+ yrs</div>
              <div className="cred-label">Experience</div>
            </li>
            <li>
              <div className="cred-num">2000+</div>
              <div className="cred-label">Sessions</div>
            </li>
            <li>
              <div className="cred-num">7 days</div>
              <div className="cred-label">Open every week</div>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

// ==================== About ====================
const About = () => (
  <section id="about" className="section-pad bg-white" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-8">
      <div className="reveal max-w-3xl">
        <span className="section-label">About Us</span>
        <h2 className="heading-lg about-h2">
          Gurugram's Trusted Physiotherapy Clinic
        </h2>
        <p className="mt-4 about-p">
          PhysioSewa is a specialised physiotherapy & rehabilitation clinic located in Sector 110, Gurugram. Founded and led by
          Dr. Anusha Dubey, we deliver personalised, evidence-based treatment for a wide range of conditions —
          from sports injuries and post-surgical rehab to chronic pain and neurological conditions. We combine
          the latest clinical techniques with genuine care, helping patients of all ages regain mobility,
          strength and quality of life.
        </p>
      </div>

      <div className="reveal mt-8 lg:mt-10" data-testid="doctor-profile-card">
        <div className="doctor-card-wrap">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            <div className="lg:col-span-5">
              <div className="flex flex-col sm:flex-row items-start gap-5">
                {/* Portrait slot — replaces AD initials until real photo is uploaded */}
                <figure className="portrait-slot" data-testid="doctor-portrait-slot" style={{ maxWidth: 200 }}>
                  <img
                    src="/assets/images/dr-anusha-dubey-physiotherapist-gurugram.webp"
                    alt="Dr. Anusha Dubey, Senior Physiotherapist at PhysioSewa, Sector 110 Gurugram"
                    width={600}
                    height={750}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  <div style={{ position: "relative", zIndex: 1 }}>
                    <div className="portrait-slot-title">Portrait placeholder</div>
                    <div className="portrait-slot-file">/assets/images/dr-anusha-dubey-physiotherapist-gurugram.webp</div>
                    <div className="portrait-slot-dims">600 × 750 · 4:5 portrait</div>
                  </div>
                </figure>
                <div className="min-w-0 flex-1">
                  <span className="doctor-tag">Founder & Lead Physiotherapist</span>
                  <div className="doctor-name mt-1.5">Dr. Anusha Dubey</div>
                  <div className="doctor-creds">BPTh, MPTh — Senior Physiotherapist</div>
                  <div className="doctor-creds" data-testid="doctor-max-antara">Trained at Max Super Speciality Hospital (Antara)</div>
                  <div className="doctor-exp">8+ years of clinical experience</div>
                </div>
              </div>

              <p className="doctor-bio mt-4">
                Dr. Anusha Dubey specialises in orthopaedic rehabilitation, sports-injury recovery, post-surgical care and women's health.
                She has personally treated <strong style={{ color: "var(--text-dark)" }}>2000+ patients</strong> across Gurugram and built
                PhysioSewa around one principle — no two patients are the same, so no two treatment plans should be either.
              </p>

              <div className="doctor-rating">
                <div className="flex" style={{ color: "var(--gold)" }} aria-hidden="true">
                  <i className="fa-solid fa-star"></i><i className="fa-solid fa-star"></i><i className="fa-solid fa-star"></i><i className="fa-solid fa-star"></i><i className="fa-solid fa-star"></i>
                </div>
                <a href={GBP_URL} target="_blank" rel="noopener noreferrer" className="text-[13px] font-medium" style={{ color: "var(--text-mid)" }} data-testid="about-google-rating">
                  5.0 ★ · 68 Google reviews
                </a>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="text-xs font-semibold mb-3 lg:mb-4" style={{ color: "var(--primary)", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Areas of Specialisation
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 lg:gap-3">
                {[
                  "Manual Therapy Expert",
                  "Post-Surgical Rehab",
                  "Sports Injury Specialist",
                  "Women's Health & Pre/Post-natal",
                  "Dry Needling Certified",
                  "Home Visit Care",
                ].map((s, i) => (
                  <div key={i} className="specialty-pill">
                    <i className="fa-solid fa-circle-check" style={{ color: "var(--primary)" }}></i>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="reveal mt-8 lg:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 lg:gap-4" data-testid="about-stats">
        {[
          { num: "2000+", label: "Patients Treated" },
          { num: "8+", label: "Years Experience" },
          { num: "20+", label: "Conditions Treated" },
          { num: "5.0★", label: "Google Rating" },
        ].map((s, i) => (
          <div key={i} className="stat-tile" data-testid={`stat-${i}`}>
            <div className="num">{s.num}</div>
            <div className="label">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ==================== Clinic Gallery (real photos) ====================
const CLINIC_PHOTOS = [
  { src: "/assets/images/clinic-storefront.webp",     alt: "PhysioSewa clinic storefront on 40 Feet Road, Sector 110, Gurugram", label: "Our clinic on 40 Feet Road" },
  { src: "/assets/images/clinic-entrance.webp",       alt: "PhysioSewa reception entrance and waiting area, Sector 110 Gurugram", label: "Reception & waiting area" },
  { src: "/assets/images/clinic-reception.webp",      alt: "PhysioSewa reception desk with Care Cure Recover branding", label: "Inside the clinic" },
  { src: "/assets/images/clinic-treatment-area.webp", alt: "PhysioSewa treatment area with therapy beds and privacy curtains", label: "Treatment bays" },
  { src: "/assets/images/clinic-exercise-area.webp",  alt: "PhysioSewa exercise and rehabilitation area with parallel bars, therapy balls and resistance bands", label: "Exercise & rehab area" },
];

const ClinicGallery = () => (
  <section id="gallery" className="section-pad bg-white" data-testid="clinic-gallery-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-8">
      <div className="text-center reveal max-w-2xl mx-auto">
        <span className="section-label">Inside PhysioSewa</span>
        <h2 className="heading-lg about-h2">
          Inside our Sector 110 clinic
        </h2>
        <p className="mt-3 about-p">
          Clean, calm and equipped for real rehabilitation — from treatment bays to parallel bars, exercise balls and resistance bands.
        </p>
      </div>
      <div className="mt-8 lg:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {CLINIC_PHOTOS.map((p, i) => (
          <figure
            key={p.src}
            className="clinic-photo reveal"
            data-testid={`clinic-photo-${i}`}
          >
            <img
              src={p.src}
              alt={p.alt}
              width={800}
              height={600}
              loading="lazy"
              decoding="async"
              className="clinic-photo-img"
            />
            <figcaption className="clinic-photo-caption">{p.label}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

// ==================== Services Conditions (6-card grid) ====================
const Conditions = () => (
  <section id="services" className="bg-white" data-testid="conditions-section">
    <div className="services-banner" data-testid="services-banner">
      <svg className="absolute inset-0 w-full h-full opacity-10 pointer-events-none" preserveAspectRatio="none" aria-hidden="true">
        <pattern id="services-dots" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="1.4" fill="white"/>
        </pattern>
        <rect width="100%" height="100%" fill="url(#services-dots)"/>
      </svg>
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
        <span
          className="section-label"
          style={{ color: "rgba(255,255,255,0.85)", marginBottom: "0.4rem" }}
        >
          What We Treat
        </span>
        <h2 className="heading-lg banner-h2">
          Comprehensive Care for Every Condition
        </h2>
        <p className="mt-2 banner-p">
          Tap any specialty below to learn about the conditions covered, our treatment approach and recovery timeline.
        </p>
      </div>
    </div>

    <div style={{ background: "var(--primary-pale)" }} className="section-pad">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {CONDITIONS.map((c, i) => (
            <Link
              to={`/physiotherapy/${c.slug}`}
              key={c.slug}
              className="service-card p-4 sm:p-6 lg:p-7 reveal block"
              style={{ textDecoration: "none" }}
              data-testid={`condition-card-${i}`}
              aria-label={`Learn more about ${c.title}`}
            >
              <div className="num-badge" data-testid={`condition-num-${i}`}>{String(i + 1).padStart(2, "0")}</div>
              <div className="icon-bubble"><i className={`fa-solid ${c.icon}`}></i></div>
              <h3 className="mt-4 service-h3" style={{ color: "var(--text-dark)" }}>
                {c.title}
              </h3>
              <p className="mt-2 service-summary">
                {c.summary}
              </p>
              <span
                className="mt-4 pt-3 service-learn-more"
                style={{ color: "var(--primary)", borderTop: "1px solid rgba(168,213,181,0.45)", marginTop: "auto" }}
              >
                Learn more <i className="fa-solid fa-arrow-right"></i>
              </span>
            </Link>
          ))}
        </div>

        {/* Services CTA — WhatsApp enquiry */}
        <div className="mt-10 text-center reveal" data-testid="services-cta-wrap">
          <a
            href={WA.services}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary-pill"
            data-testid="services-cta-btn"
          >
            <i className="fa-brands fa-whatsapp"></i> Enquire About Services
          </a>
        </div>
      </div>
    </div>
  </section>
);

// ==================== Pricing ====================
// Per-package prefilled WhatsApp deep links (all point at 9871053777).
const waPricing = (msg) => `https://wa.me/919871053777?text=${encodeURIComponent(msg)}`;

const PRICING = {
  home: {
    subtitle: "Expert care in the comfort of your home",
    plans: [
      {
        name: "Single Session",
        price: "₹900",
        perVisit: null,
        was: null,
        save: null,
        payLabel: "Pay as you go",
        badge: null,
        waLink: waPricing("Hi PhysioSewa, I would like to book a single home visit session at ₹900"),
      },
      {
        name: "7 Days",
        price: "₹6,000",
        perVisit: "₹857 / visit",
        was: "₹6,300",
        save: "Save ₹300 · 4.8% off",
        payLabel: null,
        badge: null,
        waLink: waPricing("Hi PhysioSewa, I would like to book the 7 Days Home Visit package at ₹6,000"),
      },
      {
        name: "15 Days",
        price: "₹11,000",
        perVisit: "₹733 / visit",
        was: "₹13,500",
        save: "Save ₹2,500 · 18.5% off",
        payLabel: null,
        badge: "MOST RECOMMENDED",
        waLink: waPricing("Hi PhysioSewa, I would like to book the 15 Days Home Visit package at ₹11,000"),
      },
      {
        name: "30 Days",
        price: "₹21,000",
        perVisit: "₹700 / visit",
        was: "₹27,000",
        save: "Save ₹6,000 · 22.2% off",
        payLabel: null,
        badge: null,
        waLink: waPricing("Hi PhysioSewa, I would like to book the 30 Days Home Visit package at ₹21,000"),
      },
    ],
  },
  clinic: {
    subtitle: "Personalised care at our clinic in Sector 110",
    plans: [
      {
        name: "Single Session",
        price: "₹800",
        perVisit: null,
        was: null,
        save: null,
        payLabel: "Pay as you go",
        badge: null,
        waLink: waPricing("Hi PhysioSewa, I would like to book a single clinic session at ₹800"),
      },
      {
        name: "7 Days",
        price: "₹5,300",
        perVisit: "₹757 / visit",
        was: "₹5,600",
        save: "Save ₹300 · 5.4% off",
        payLabel: null,
        badge: null,
        waLink: waPricing("Hi PhysioSewa, I would like to book the 7 Days Clinic package at ₹5,300"),
      },
      {
        name: "15 Days",
        price: "₹10,000",
        perVisit: "₹667 / visit",
        was: "₹12,000",
        save: "Save ₹2,000 · 16.7% off",
        payLabel: null,
        badge: "BEST VALUE",
        waLink: waPricing("Hi PhysioSewa, I would like to book the 15 Days Clinic package at ₹10,000"),
      },
      {
        name: "30 Days",
        price: "₹18,000",
        perVisit: "₹600 / visit",
        was: "₹24,000",
        save: "Save ₹6,000 · 25% off",
        payLabel: null,
        badge: null,
        waLink: waPricing("Hi PhysioSewa, I would like to book the 30 Days Clinic package at ₹18,000"),
      },
    ],
  },
};

const INCLUSIONS = [
  "45-minute session",
  "Certified physiotherapist",
  "Personalised treatment plan",
  "Progress tracking at every stage",
];

const Pricing = () => {
  const [tab, setTab] = useState("home");
  const { plans, subtitle } = PRICING[tab];

  return (
    <section id="pricing" className="section-pad bg-white" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center reveal">
          <span className="section-label">Transparent Pricing</span>
          <h2 className="heading-lg about-h2">Physiotherapy Packages</h2>
          <p className="mt-3 about-p">
            Every price published. No consultation fee just to find out the cost.
          </p>
          <div className="mt-6 inline-block">
            <div className="price-tabs" role="tablist" data-testid="pricing-tabs">
              <button onClick={() => setTab("home")} className={tab === "home" ? "active" : ""} data-testid="tab-home">Home Visit</button>
              <button onClick={() => setTab("clinic")} className={tab === "clinic" ? "active" : ""} data-testid="tab-clinic">At Our Clinic</button>
            </div>
          </div>
          <p className="mt-4 text-[14px] font-medium" style={{ color: "var(--text-mid)" }} data-testid="pricing-subtitle">
            {subtitle}
          </p>
        </div>

        {/* Fixed order Single → 7 → 15 → 30 on every breakpoint */}
        <div className="mt-10 lg:mt-12 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 items-stretch">
          {plans.map((p, i) => (
            <PriceCard key={`${tab}-${p.name}`} p={p} tab={tab} idx={i} />
          ))}
        </div>

        {/* Terms line per tab, min 14px */}
        <p
          className="mt-6 text-[14px] text-center"
          style={{ color: "var(--text-mid)", lineHeight: 1.6 }}
          data-testid="pricing-terms"
        >
          Packages are non-transferable and valid for consecutive days. Prices include all charges — no hidden fees.
        </p>

        <div
          className="mt-5 text-center text-[14px] flex flex-wrap justify-center gap-x-5 gap-y-2"
          style={{ color: "var(--text-mid)" }}
          data-testid="pricing-reassurance"
        >
          <span><i className="fa-solid fa-circle-check mr-1.5" style={{ color: "var(--primary)" }}></i>Same physiotherapist every session</span>
          <span><i className="fa-solid fa-circle-check mr-1.5" style={{ color: "var(--primary)" }}></i>Cancel anytime</span>
        </div>

        <div className="mt-8 text-center reveal" data-testid="pricing-free-consult-wrap">
          <a
            href={WA.freeConsult}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline-pill"
            data-testid="pricing-free-consult-btn"
          >
            <i className="fa-brands fa-whatsapp" style={{ color: "#25D366" }}></i> Book your free assessment
          </a>
        </div>
      </div>
    </section>
  );
};

const PriceCard = ({ p, tab, idx }) => {
  const highlighted = !!p.badge;
  return (
    <div
      className={`price-card reveal ${highlighted ? "popular-dark" : ""} flex flex-col`}
      data-testid={`price-card-${tab}-${idx}`}
      style={{ height: "100%", position: "relative" }}
    >
      {highlighted && <div className="popular-band" data-testid={`price-badge-${tab}-${idx}`}>{p.badge}</div>}

      <div
        className="text-[12px] font-semibold tracking-wide"
        style={{
          color: highlighted ? "rgba(255,255,255,0.85)" : "var(--text-mid)",
          textTransform: "uppercase", letterSpacing: "0.12em",
        }}
      >
        {p.name}
      </div>

      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <span
          style={{
            fontSize: "2.1rem", fontWeight: 800,
            fontVariantNumeric: "tabular-nums",
            color: highlighted ? "#FFFFFF" : "var(--text-dark)",
            lineHeight: 1,
          }}
        >
          {p.price}
        </span>
        {p.was && (
          <span
            style={{
              textDecoration: "line-through",
              color: highlighted ? "rgba(255,255,255,0.55)" : "var(--text-light)",
              fontSize: "0.95rem",
            }}
          >
            {p.was}
          </span>
        )}
      </div>

      {p.perVisit && (
        <div className="mt-2">
          <span
            className="inline-block px-2.5 py-1 rounded-full text-[13px] font-semibold"
            style={{
              background: highlighted ? "rgba(255,255,255,0.15)" : "var(--primary-pale)",
              color: highlighted ? "#FFFFFF" : "var(--primary)",
              border: highlighted ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(168,213,181,0.55)",
            }}
          >
            {p.perVisit}
          </span>
        </div>
      )}

      {p.save && (
        <div className="mt-2 text-[13px] font-semibold" style={{ color: highlighted ? "var(--primary-light)" : "var(--primary)" }}>
          {p.save}
        </div>
      )}

      {p.payLabel && (
        <div className="mt-2 text-[13px] font-medium" style={{ color: highlighted ? "rgba(255,255,255,0.8)" : "var(--text-mid)" }}>
          {p.payLabel}
        </div>
      )}

      <div className="my-4" style={{ height: 1, background: highlighted ? "rgba(255,255,255,0.18)" : "rgba(168,213,181,0.55)" }}/>

      <ul className="space-y-2 text-[14px] flex-1" style={{ color: highlighted ? "rgba(255,255,255,0.92)" : "var(--text-mid)" }}>
        {INCLUSIONS.map((inc, k) => (
          <li key={k}>
            <i className="fa-solid fa-check mr-2" style={{ color: highlighted ? "var(--primary-light)" : "var(--primary)" }}></i>
            {inc}
          </li>
        ))}
      </ul>

      <a
        href={p.waLink}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold transition-all"
        style={{
          marginTop: "auto",
          ...(highlighted
            ? { background: "#FFFFFF", color: "var(--dark-footer)", border: "2px solid #FFFFFF", minHeight: 48 }
            : { background: "transparent", color: "var(--primary)", border: "2px solid var(--primary)", minHeight: 48 }),
        }}
        data-testid={`book-now-${tab}-${idx}`}
      >
        Book Now
      </a>
    </div>
  );
};

// ==================== Why Us ====================
const WhyUs = () => (
  <section id="why" className="section-pad bg-white" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-8">
      <div className="text-center reveal max-w-2xl mx-auto">
        <span className="section-label">Why Us</span>
        <h2 className="heading-lg about-h2" style={{ color: "var(--text-dark)" }}>
          Why Patients Choose PhysioSewa
        </h2>
      </div>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8">
        {[
          { icon: "fa-bullseye", title: "Personalised Treatment Plans", desc: "No generic protocols. Every plan tailored to your specific condition, lifestyle and recovery goals." },
          { icon: "fa-microscope", title: "Advanced Equipment", desc: "Latest electrotherapy machines, ultrasound, dry needling and manual therapy tools." },
          { icon: "fa-car-side", title: "Home Visit Service", desc: "Can't travel? We come to you. Same expert care delivered at your home, office or hospital room." },
          { icon: "fa-clipboard-list", title: "Evidence-Based Practice", desc: "All treatments follow international clinical guidelines and the latest physiotherapy research." },
        ].map((b, i) => (
          <div key={i} className="why-block-light reveal" data-testid={`why-block-${i}`}>
            <div className="why-icon"><i className={`fa-solid ${b.icon}`}></i></div>
            <h3 className="why-h3" style={{ color: "var(--text-dark)" }}>{b.title}</h3>
            <p className="why-p">{b.desc}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 text-center reveal" data-testid="why-cta-wrap">
        <a
          href={WA.book}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary-pill"
          data-testid="why-cta-btn"
        >
          Book Appointment <i className="fa-solid fa-arrow-right"></i>
        </a>
      </div>
    </div>
  </section>
);

// ==================== Process ====================
const Process = () => (
  <section className="section-pad" style={{ background: "var(--primary-pale)" }} data-testid="process-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-8">
      <div className="text-center reveal max-w-2xl mx-auto">
        <span className="section-label">The Process</span>
        <h2 className="heading-lg about-h2">
          Start Your Recovery in 3 Simple Steps
        </h2>
      </div>

      <div className="mt-10 md:hidden flex flex-col gap-5 relative">
        {[
          { n: "1", icon: "fa-calendar-check", title: "Book an Appointment", desc: "Message us on WhatsApp. We confirm within 30 minutes." },
          { n: "2", icon: "fa-magnifying-glass-chart", title: "Assessment & Diagnosis", desc: "Our expert evaluates your condition and designs your personalised treatment plan." },
          { n: "3", icon: "fa-dumbbell", title: "Treatment & Recovery", desc: "Begin your sessions and track measurable improvement at every stage." },
        ].map((s, i, arr) => (
          <div key={i} className="reveal flex gap-4 relative" data-testid={`step-mobile-${i}`}>
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="step-circle" style={{ width: 52, height: 52, fontSize: "1.2rem" }}>{s.n}</div>
              {i < arr.length - 1 && (
                <div style={{ flex: 1, width: 2, borderLeft: "2px dashed var(--dark-footer)", marginTop: 6, minHeight: 28 }}/>
              )}
            </div>
            <div className="pt-1.5 pb-3">
              <h3 className="text-[17px] font-bold" style={{ color: "var(--text-dark)" }}>{s.title}</h3>
              <p className="mt-1.5 text-[14px]" style={{ color: "var(--text-mid)", lineHeight: 1.6 }}>{s.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 hidden md:grid md:grid-cols-3 gap-6 md:gap-2 items-start relative">
        {[
          { n: "1", icon: "fa-calendar-check", title: "Book an Appointment", desc: "Message us on WhatsApp. We confirm within 30 minutes." },
          { n: "2", icon: "fa-magnifying-glass-chart", title: "Assessment & Diagnosis", desc: "Our expert evaluates your condition and designs your personalised treatment plan." },
          { n: "3", icon: "fa-dumbbell", title: "Treatment & Recovery", desc: "Begin your sessions and track measurable improvement at every stage." },
        ].map((s, i, arr) => (
          <div key={i} className="reveal flex flex-col items-center text-center px-3 relative" data-testid={`step-${i}`}>
            <div className="step-circle">{s.n}</div>
            <h3 className="mt-4 text-lg font-bold" style={{ color: "var(--text-dark)" }}>{s.title}</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--text-mid)", maxWidth: 280, lineHeight: 1.6 }}>{s.desc}</p>
            {i < arr.length - 1 && (
              <div className="hidden md:block absolute top-[32px] right-[-50%] w-full"
                   style={{ borderTop: "2px dashed var(--dark-footer)", zIndex: 0 }} aria-hidden="true"/>
            )}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ==================== Testimonials ====================
const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const [perView, setPerView] = useState(3);
  const trackRef = useRef(null);

  useEffect(() => {
    const calc = () => setPerView(window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx(prev => (prev + 1) % Math.max(1, TESTIMONIALS.length - perView + 1));
    }, 5000);
    return () => clearInterval(t);
  }, [perView]);

  const maxIdx = Math.max(0, TESTIMONIALS.length - perView);
  const safeIdx = Math.min(idx, maxIdx);
  const totalDots = maxIdx + 1;

  return (
    <section id="testimonials" className="section-pad bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 reveal">
          <div>
            <span className="section-label">Patient Stories</span>
            <h2 className="heading-lg about-h2">What Our Patients Say</h2>
          </div>
          <div className="flex gap-2">
            <button className="carousel-arrow" onClick={() => setIdx(Math.max(0, safeIdx - 1))} disabled={safeIdx === 0} aria-label="Previous" data-testid="testi-prev">
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button className="carousel-arrow" onClick={() => setIdx(Math.min(maxIdx, safeIdx + 1))} disabled={safeIdx === maxIdx} aria-label="Next" data-testid="testi-next">
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <div className="mt-8 lg:mt-10 overflow-hidden">
          <div
            ref={trackRef}
            className="flex transition-transform duration-700 ease-out"
            style={{ transform: `translateX(-${safeIdx * (100 / perView)}%)` }}
          >
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="flex-shrink-0 px-2 sm:px-3" style={{ width: `${100 / perView}%` }} data-testid={`testimonial-${i}`}>
                <div className="testimonial-card h-full">
                  <div className="quote-mark">"</div>
                  <div className="stars pt-6">
                    {Array.from({ length: t.stars }).map((_, k) => <i key={k} className="fa-solid fa-star"></i>)}
                  </div>
                  <p style={{ color: "var(--text-mid)", lineHeight: 1.7, fontSize: "14px" }}>{t.text}</p>
                  <div className="mt-5 pt-4 border-t flex items-center gap-3" style={{ borderColor: "rgba(168,213,181,0.4)" }}>
                    <div className="monogram" data-testid={`testi-monogram-${i}`} aria-hidden="true">
                      {(t.name.trim()[0] || "P").toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, color: "var(--text-dark)", fontSize: "15px" }}>{t.name}</div>
                      <div className="text-[13px]" style={{ color: "var(--text-light)" }}>{t.loc}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 flex justify-center gap-2" data-testid="testi-dots">
          {Array.from({ length: totalDots }).map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Go to testimonial group ${i + 1}`}
              className="testi-dot"
              data-active={i === safeIdx ? "true" : "false"}
              data-testid={`testi-dot-${i}`}
            />
          ))}
        </div>

        <div className="mt-10 text-center reveal" data-testid="testi-cta-wrap">
          <a
            href={WA.book}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary-pill"
            data-testid="testi-cta-btn"
          >
            Book Appointment <i className="fa-solid fa-arrow-right"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

// ==================== Callback form (quiet fallback under WhatsApp CTA) ====================
const SPECIALTY_OPTIONS = [
  { value: "", label: "Choose one…" },
  { value: "Pain Management", label: "Pain Management" },
  { value: "Neurological Rehabilitation", label: "Neurological Rehabilitation" },
  { value: "Geriatric Rehabilitation", label: "Geriatric Rehabilitation" },
  { value: "Orthopaedic & Sports Rehabilitation", label: "Orthopaedic & Sports Rehabilitation" },
  { value: "Maternity Care", label: "Maternity Care" },
  { value: "Manual Therapy", label: "Manual Therapy" },
  { value: "Something else", label: "Something else" },
];

const TIME_OPTIONS = ["Morning", "Afternoon", "Evening"];

const CallbackForm = () => {
  const [form, setForm] = useState({
    name: "", phone: "", specialty: "", timeSlot: "", notes: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm(s => ({ ...s, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Enter your name so we know who we're calling.";
    const digits = form.phone.replace(/\D/g, "");
    if (!digits) e.phone = "Enter your phone number.";
    else if (digits.length !== 10) e.phone = "Enter a 10-digit mobile number, e.g. 9876543210.";
    if (!form.specialty) e.specialty = "Pick the closest option — we can adjust when we call.";
    if (!form.timeSlot) e.timeSlot = "Pick a preferred time — morning, afternoon or evening.";
    if (form.notes.length > 300) e.notes = "Please keep notes under 300 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await submitBooking({
        full_name: form.name,
        phone: form.phone,
        service: form.specialty,
        visit_type: "callback",
        preferred_date: new Date().toISOString().slice(0, 10),
        preferred_time: form.timeSlot,
        notes: form.notes || "(callback form request)",
      });
      setDone(true);
    } catch (err) {
      // Silent — submitBooking already logs to backend + opens WA fallback; we still confirm success.
      console.warn("Callback form submit warning:", err);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="callback-success" data-testid="callback-form-success">
        <div className="callback-success-icon" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
        <h4>Thanks — we'll ring you back.</h4>
        <p>
          A member of the PhysioSewa team will call you on <strong>{form.phone}</strong> during
          your <strong>{form.timeSlot.toLowerCase()}</strong> window, within one working day (clinic hours: 9 AM – 9 PM, every day).
        </p>
      </div>
    );
  }

  return (
    <form className="callback-form" onSubmit={handleSubmit} noValidate data-testid="callback-form">
      <div className="callback-form-header">
        <h4>Prefer a callback?</h4>
        <p>Leave your details and we'll ring you back.</p>
      </div>

      <label className="cbf-field">
        <span className="cbf-label">Name</span>
        <input
          type="text"
          value={form.name}
          onChange={update("name")}
          autoComplete="name"
          className="cbf-input"
          data-testid="callback-name"
          aria-invalid={!!errors.name}
        />
        {errors.name && <span className="cbf-error" data-testid="callback-name-error">{errors.name}</span>}
      </label>

      <label className="cbf-field">
        <span className="cbf-label">Phone</span>
        <input
          type="tel"
          inputMode="numeric"
          value={form.phone}
          onChange={update("phone")}
          autoComplete="tel"
          placeholder="10-digit mobile number"
          className="cbf-input"
          data-testid="callback-phone"
          aria-invalid={!!errors.phone}
        />
        {errors.phone && <span className="cbf-error" data-testid="callback-phone-error">{errors.phone}</span>}
      </label>

      <label className="cbf-field">
        <span className="cbf-label">What's troubling you?</span>
        <select
          value={form.specialty}
          onChange={update("specialty")}
          className="cbf-input"
          data-testid="callback-specialty"
          aria-invalid={!!errors.specialty}
        >
          {SPECIALTY_OPTIONS.map(o => (
            <option key={o.value} value={o.value} disabled={o.value === ""}>{o.label}</option>
          ))}
        </select>
        {errors.specialty && <span className="cbf-error" data-testid="callback-specialty-error">{errors.specialty}</span>}
      </label>

      <fieldset className="cbf-field">
        <legend className="cbf-label">Preferred time</legend>
        <div className="cbf-radios" role="radiogroup" data-testid="callback-time-group">
          {TIME_OPTIONS.map(t => (
            <label key={t} className={`cbf-radio ${form.timeSlot === t ? "is-selected" : ""}`}>
              <input
                type="radio"
                name="callback-time"
                value={t}
                checked={form.timeSlot === t}
                onChange={update("timeSlot")}
                data-testid={`callback-time-${t.toLowerCase()}`}
              />
              <span>{t}</span>
            </label>
          ))}
        </div>
        {errors.timeSlot && <span className="cbf-error" data-testid="callback-time-error">{errors.timeSlot}</span>}
      </fieldset>

      <label className="cbf-field">
        <span className="cbf-label">Notes <span className="cbf-optional">(optional, up to 300 characters)</span></span>
        <textarea
          value={form.notes}
          onChange={update("notes")}
          rows={3}
          maxLength={320}
          className="cbf-input"
          data-testid="callback-notes"
          aria-invalid={!!errors.notes}
        />
        <span className="cbf-hint">{form.notes.length}/300</span>
        {errors.notes && <span className="cbf-error" data-testid="callback-notes-error">{errors.notes}</span>}
      </label>

      <button type="submit" disabled={submitting} className="cbf-submit" data-testid="callback-submit">
        {submitting ? "Sending…" : "Request a callback"}
      </button>

      <p className="cbf-consent" data-testid="callback-consent">
        We'll only use your number to contact you about your enquiry.
      </p>
    </form>
  );
};

// ==================== Contact (single WhatsApp-first contact section) ====================
const Contact = () => (
  <section id="contact" className="section-pad bg-white" data-testid="contact-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-8">
      {/* Big WhatsApp CTA card */}
      <div
        className="rounded-[20px] p-6 md:p-10 text-center reveal"
        style={{
          background: "linear-gradient(135deg, #1B3A2D 0%, #2E5A44 60%, #6DBF8A 100%)",
          color: "white",
          boxShadow: "0 16px 38px rgba(27,58,45,0.18)",
        }}
        data-testid="contact-cta-card"
      >
        <span className="section-label" style={{ color: "var(--primary-light)" }}>Get In Touch</span>
        <h2 className="heading-lg about-h2" style={{ color: "white" }}>
          Ready to Start Your Recovery?
        </h2>
        <p className="mt-3 text-[14px] md:text-[16px] max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.9)", lineHeight: 1.7 }}>
          Message us on WhatsApp — we'll reply within 30 minutes during clinic hours. No forms, no waiting.
        </p>
        <a
          href={WA.contact}
          target="_blank"
          rel="noopener noreferrer"
          className="contact-wa-btn mt-6"
          data-testid="contact-whatsapp-btn"
        >
          <i className="fa-brands fa-whatsapp"></i> Chat With Us on WhatsApp
        </a>
        <div className="mt-5 text-[13px]" style={{ color: "rgba(255,255,255,0.75)" }}>
          or call <a href={`tel:${PHONE_TEL_1}`} style={{ color: "white", fontWeight: 600, textDecoration: "underline" }} data-testid="contact-phone-inline">{PHONE_1}</a>
        </div>
      </div>

      {/* Quiet fallback: callback form for anyone without WhatsApp */}
      <div className="max-w-2xl mx-auto mt-8 reveal" data-testid="callback-form-wrap">
        <CallbackForm />
      </div>

      {/* Info + Map grid */}
      <div className="mt-10 lg:mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
        <div className="reveal order-first lg:order-last">
          <iframe
            title="PhysioSewa Sector 110 Gurugram"
            src="https://maps.google.com/maps?q=PhysioSewa+B-71+40+Feet+Road+New+Palam+Vihar+Phase+1+Sector+110+Gurugram+Haryana+122017&t=&z=16&ie=UTF8&iwloc=B&output=embed"
            width="100%"
            height="220"
            className="contact-map-iframe"
            style={{ border: 0, borderRadius: "16px", boxShadow: "var(--shadow-soft)" }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="contact-map"
          />
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=B-71+40+Feet+Road+New+Palam+Vihar+Phase+1+Sector+110+Gurugram+Haryana+122017"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary-pill mt-4 inline-flex"
            style={{ minHeight: 44 }}
            data-testid="get-directions-btn"
          >
            Get directions
          </a>
        </div>
        <div className="reveal">
          <h3 className="heading-lg contact-h3">Clinic Details</h3>
          <ul className="mt-5 space-y-3.5 text-[14px] md:text-[15px]" style={{ color: "var(--text-mid)" }}>
            <li className="flex items-start gap-3"><i className="fa-solid fa-location-dot mt-1" style={{ color: "var(--primary)" }}></i>
              <span>
                B-71, 40 Feet Road,<br/>
                New Palam Vihar Phase 1,<br/>
                Near Dwarka Expressway,<br/>
                Sector 110, Gurugram 122017, Haryana
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-solid fa-phone mt-1" style={{ color: "var(--primary)" }}></i>
              <span>
                <a href={`tel:${PHONE_TEL_1}`} style={{ color: "var(--text-dark)", fontWeight: 600 }} data-testid="contact-phone-1">{PHONE_1}</a>
                <span style={{ margin: "0 8px", color: "var(--text-light)" }}>|</span>
                <a href={`tel:${PHONE_TEL_2}`} style={{ color: "var(--text-dark)", fontWeight: 600 }} data-testid="contact-phone-2">{PHONE_2}</a>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <i className="fa-brands fa-whatsapp mt-1" style={{ color: "#25D366" }}></i>
              <a href={WA.contact} target="_blank" rel="noopener noreferrer" style={{ color: "var(--text-dark)", fontWeight: 600 }} data-testid="contact-whatsapp-inline">
                Chat on WhatsApp
              </a>
            </li>
            <li className="flex items-start gap-3"><i className="fa-solid fa-envelope mt-1" style={{ color: "var(--primary)" }}></i> <a href={`mailto:${EMAIL}`} style={{ color: "var(--text-dark)", fontWeight: 600 }}>{EMAIL}</a></li>
            <li className="flex items-start gap-3"><i className="fa-solid fa-globe mt-1" style={{ color: "var(--primary)" }}></i> www.physiosewa.in</li>
            <li className="flex items-start gap-3"><i className="fa-solid fa-clock mt-1" style={{ color: "var(--primary)" }}></i> Every day: 9:00 AM – 9:00 PM</li>
          </ul>
          <div className="mt-5 flex gap-3">
            {[
              { icon: "fa-instagram", href: INSTAGRAM_URL, label: "Instagram" },
              { icon: "fa-whatsapp", href: WA.enquiry, label: "WhatsApp" },
            ].map((s, i) => (
              <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                 className="w-11 h-11 rounded-full flex items-center justify-center transition-all"
                 style={{ background: "var(--primary-pale)", color: "var(--primary)", border: "1.5px solid rgba(168,213,181,0.6)" }}
                 data-testid={`social-${s.label.toLowerCase()}`}>
                <i className={`fa-brands ${s.icon}`}></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ==================== FAQ ====================
const FAQS = [
  { q: "What conditions does PhysioSewa treat?", a: "We treat orthopaedic and sports injuries, back and neck pain, neurological conditions (stroke, Parkinson's), post-surgical recovery, women's health and maternity care, geriatric rehabilitation and manual-therapy specialties (dry needling, cupping)." },
  { q: "Do you offer home visits in Gurugram?", a: "Yes! We provide home physiotherapy across Gurugram including Sectors 90–115, DLF phases, Sohna Road and surrounding areas." },
  { q: "Do I need a doctor's referral to book?", a: "No referral needed. You can book directly by messaging us on WhatsApp or calling 9871053777 / 9217221357." },
  { q: "How long is a typical session?", a: "Each session is approximately 45 minutes. Your therapist will advise the frequency based on your condition." },
  { q: "What is the cost of physiotherapy at PhysioSewa?", a: "At-clinic sessions start from ₹800 per session. Home visits start from ₹950. Multi-session packages offer savings of up to 35%." },
  { q: "What are your clinic hours?", a: "We are open 7 days a week, 9:00 AM to 9:00 PM including Sundays and public holidays." },
  { q: "Is physiotherapy painful?", a: "Some discomfort is normal during treatment, especially for acute injuries. Your therapist will work within your comfort level and adjust treatment accordingly." },
  { q: "How do I reach PhysioSewa in Sector 110, Gurugram?", a: "Our clinic is at B-71, 40 Feet Road, New Palam Vihar Phase 1, Near Dwarka Expressway, Sector 110, Gurugram 122017, Haryana. Call 9871053777 for exact directions." },
];

const FAQ = () => (
  <section id="faq" className="section-pad" style={{ background: "var(--primary-pale)" }} data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-4 lg:px-8">
      <div className="text-center reveal">
        <span className="section-label">Got Questions?</span>
        <h2 className="heading-lg about-h2">Frequently Asked Questions</h2>
      </div>
      <div className="mt-8 lg:mt-10 reveal">
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="faq-item border-0" data-testid={`faq-${i}`}>
              <AccordionTrigger className="faq-trigger hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="px-5 pb-4 pt-0 text-[14px]" style={{ color: "var(--text-mid)", lineHeight: 1.7 }}>
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

// ==================== Footer ====================
const Footer = () => (
  <footer className="footer-bg pt-12 pb-6 lg:pt-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
      <div>
        <Logo light/>
        <p className="mt-4 text-[14px]" style={{ lineHeight: 1.65 }}>
          <em style={{ color: "var(--primary-light)", fontStyle: "normal", fontWeight: 600 }}>"{TAGLINE}"</em>
        </p>
        <p className="mt-3 text-[14px]" style={{ lineHeight: 1.65 }}>
          Advanced physiotherapy & rehabilitation clinic in Sector 110, Gurugram. Restoring movement and rebuilding lives since day one.
        </p>
        <a
          href={WA.book}
          target="_blank"
          rel="noopener noreferrer"
          className="footer-book-btn mt-5"
          data-testid="footer-book-btn"
        >
          <i className="fa-brands fa-whatsapp"></i> Book Appointment
        </a>
      </div>
      <div>
        <h4 className="font-bold mb-3 lg:mb-4">Quick Links</h4>
        <ul className="grid grid-cols-2 md:grid-cols-1 gap-x-4 gap-y-2 text-[14px]">
          {[
            { id: "home", label: "Home" },
            { id: "services", label: "Services" },
            { id: "about", label: "About" },
            { id: "pricing", label: "Pricing" },
            { id: "testimonials", label: "Testimonials" },
            { id: "contact", label: "Contact" },
          ].map(l => (
            <li key={l.id}>
              <a href={`#${l.id}`} onClick={(e) => { e.preventDefault(); document.getElementById(l.id)?.scrollIntoView({ behavior: "smooth" }); }}>
                {l.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-bold mb-3 lg:mb-4">Contact</h4>
        <ul className="space-y-2 text-[14px]">
          <li>
            <i className="fa-solid fa-phone mr-2"></i>
            <a href={`tel:${PHONE_TEL_1}`} data-testid="footer-phone-1">{PHONE_1}</a>
          </li>
          <li><i className="fa-solid fa-envelope mr-2"></i><a href={`mailto:${EMAIL}`}>{EMAIL}</a></li>
          <li className="flex items-start gap-2">
            <i className="fa-solid fa-location-dot mt-1"></i>
            <span>B-71, 40 Feet Road, New Palam Vihar Phase 1, Sector 110, Gurugram 122017</span>
          </li>
          <li><i className="fa-solid fa-clock mr-2"></i>{HOURS_FULL}</li>
        </ul>
        <div className="mt-4 flex gap-3">
          {[
            { icon: "fa-instagram", href: INSTAGRAM_URL, label: "Instagram" },
            { icon: "fa-whatsapp", href: WA.enquiry, label: "WhatsApp" },
          ].map((s, i) => (
            <a key={i} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
               className="w-10 h-10 rounded-full flex items-center justify-center"
               style={{ background: "rgba(255,255,255,0.08)", color: "var(--primary-light)" }}>
              <i className={`fa-brands ${s.icon}`}></i>
            </a>
          ))}
        </div>
      </div>
    </div>
    <div className="mt-10 lg:mt-12 pt-5 lg:pt-6 max-w-7xl mx-auto px-4 lg:px-8" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-[12px] text-center" style={{ color: "rgba(255,255,255,0.7)" }} data-testid="footer-bottom-bar">
        <span>© {new Date().getFullYear()} PhysioSewa. All Rights Reserved.</span>
        <span style={{ opacity: 0.4 }}>|</span>
        <Link to="/privacy" style={{ color: "rgba(255,255,255,0.7)" }} data-testid="footer-privacy-link">Privacy Policy</Link>
        <span style={{ opacity: 0.4 }}>|</span>
        <span>Designed with <span style={{ color: "#FF6B6B" }}>♥</span> in Gurugram</span>
      </div>
    </div>
  </footer>
);

// ==================== Your First Visit (dark green band, 4 steps) ====================
const FIRST_VISIT_STEPS = [
  { n: "1", title: "Assessment",     desc: "History, movement testing, identifying the actual cause. Roughly 30 to 40 minutes." },
  { n: "2", title: "Explanation",    desc: "Dr. Anusha explains what is happening in plain language, and shows you." },
  { n: "3", title: "Your plan",      desc: "Expected number of sessions, what you do at home, what progress should look like." },
  { n: "4", title: "First treatment",desc: "Treatment starts the same day wherever appropriate." },
];
const YourFirstVisit = () => (
  <section id="first-visit" className="first-visit-section" data-testid="first-visit-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-8">
      <div className="text-center reveal max-w-2xl mx-auto">
        <span className="section-label" style={{ color: "var(--primary-light)" }}>The Process</span>
        <h2 className="heading-lg about-h2" style={{ color: "white" }}>What happens on your first visit</h2>
      </div>
      <div className="first-visit-grid mt-8 lg:mt-10">
        {FIRST_VISIT_STEPS.map((s, i) => (
          <div key={s.n} className="first-visit-step reveal" data-testid={`first-visit-step-${i}`}>
            <div className="first-visit-num">{s.n}</div>
            <h3 className="first-visit-title">{s.title}</h3>
            <p className="first-visit-desc">{s.desc}</p>
          </div>
        ))}
      </div>
      <p className="first-visit-footer" data-testid="first-visit-footer">
        Your first assessment is free. Treatment sessions are charged from your first treatment onwards.
      </p>
    </div>
  </section>
);

// ==================== Areas We Serve (replaces the keyword-stuffed grey block) ====================
const AREAS = [
  { name: "Sector 110 & New Palam Vihar",       meta: "Our home base — same-day home visits" },
  { name: "Sectors 102–109, Dwarka Expressway", meta: "10–15 minutes · same-day home visits" },
  { name: "Palam Vihar & Bajghera",             meta: "Around 20 minutes" },
  { name: "Sheetla Mata Road & Vatika Chowk",   meta: "Around 20–25 minutes" },
  { name: "Sohna Road & Southern Peripheral Road", meta: "Around 25–30 minutes" },
  { name: "Dwarka Sectors 21, 22, 23",          meta: "Around 25 minutes · Delhi border" },
  { name: "Bijwasan & Kapashera",               meta: "Around 20–25 minutes · Delhi" },
];

const AreasWeServe = () => (
  <section id="areas" className="areas-section" data-testid="areas-section">
    <div className="max-w-7xl mx-auto px-4 lg:px-8">
      <div className="text-center reveal max-w-2xl mx-auto">
        <span className="section-label">Areas We Serve</span>
        <h2 className="heading-lg about-h2">Home physiotherapy across west Gurugram</h2>
        <p className="mt-3 about-p">
          Same physiotherapist to your door. Travel times are approximate and depend on time of day.
        </p>
      </div>
      <ul className="areas-list reveal" data-testid="areas-list">
        {AREAS.map((a) => (
          <li key={a.name} className="area-item">
            <span className="area-icon" aria-hidden="true"><i className="fa-solid fa-location-dot"></i></span>
            <span>
              <strong>{a.name}</strong>
              <span className="area-meta">{a.meta}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="areas-footer" data-testid="areas-footer">
        Not on the list? <a href={WA.enquiry} target="_blank" rel="noopener noreferrer" style={{ color: "var(--primary)", fontWeight: 600 }}>Message us</a> — we cover most of west Gurugram and nearby Delhi.
      </p>
    </div>
  </section>
);

// ==================== Sticky mobile action bar ====================
const StickyMobileBar = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById("home");
      const heroHeight = hero ? hero.offsetHeight : 600;
      setVisible(window.scrollY > heroHeight - 100);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Add / remove padding-bottom guard on <body>
  useEffect(() => {
    if (visible) document.body.classList.add("has-mobile-bar");
    else document.body.classList.remove("has-mobile-bar");
    return () => document.body.classList.remove("has-mobile-bar");
  }, [visible]);

  return (
    <div
      className={`mobile-action-bar ${visible ? "visible" : ""}`}
      role="region"
      aria-label="Quick contact actions"
      data-testid="mobile-action-bar"
      aria-hidden={!visible}
    >
      <a
        href={`tel:${PHONE_TEL_1}`}
        className="mab-call"
        data-testid="mab-call"
        aria-label="Call PhysioSewa"
      >
        <i className="fa-solid fa-phone"></i> Call
      </a>
      <a
        href={WA.book}
        target="_blank"
        rel="noopener noreferrer"
        className="mab-wa"
        data-testid="mab-whatsapp"
        aria-label="WhatsApp PhysioSewa"
      >
        <i className="fa-brands fa-whatsapp"></i> WhatsApp
      </a>
    </div>
  );
};

// ==================== Floating buttons ====================
const FloatingButtons = () => {
  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <>
      <a href={WA.base} target="_blank" rel="noopener noreferrer" className="fab-whatsapp"
         aria-label="Chat with us on WhatsApp" title="Chat with us on WhatsApp" data-testid="fab-whatsapp">
        <i className="fa-brands fa-whatsapp"></i>
      </a>
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="fab-totop" aria-label="Back to top" data-testid="fab-totop">
          <i className="fa-solid fa-arrow-up"></i>
        </button>
      )}
    </>
  );
};

// ==================== Reveal observer hook ====================
const useReveal = () => {
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in-view"); });
    }, { threshold: 0.12 });

    const observeAll = () => {
      document.querySelectorAll(".reveal:not(.in-view)").forEach(el => obs.observe(el));
    };
    observeAll();

    const mo = new MutationObserver(observeAll);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => { obs.disconnect(); mo.disconnect(); };
  }, []);
};

// ==================== Page ====================
const PAGE_TITLES = {
  default:      "Physiotherapy in Sector 110 Gurugram | PhysioSewa",
  services:     "PhysioSewa Services — Back Pain, Knee Pain, Sports Rehab Gurugram",
  about:        "About Dr. Anusha Dubey — Physiotherapist Gurugram, Sector 110",
  pricing:      "Physiotherapy Pricing Gurugram — Clinic & Home Visit Packages",
  contact:      "Contact PhysioSewa — Physiotherapy Clinic Sector 110 Gurugram",
  why:          "Why Choose PhysioSewa — Best Physiotherapist Dwarka Expressway",
  testimonials: "Patient Reviews — PhysioSewa Physiotherapy Gurugram",
};

export { PAGE_TITLES };

export default function Landing({ section, title } = {}) {
  useReveal();
  const [bannerVisible, setBannerVisible] = useState(true);

  // Set document title per virtual route
  useEffect(() => {
    document.title = title || PAGE_TITLES.default;
  }, [title]);

  // Scroll to requested section on mount
  useEffect(() => {
    if (!section) return;
    // Let the page render, then scroll to the section
    const t = setTimeout(() => {
      const el = document.getElementById(section);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(t);
  }, [section]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--banner-height",
      bannerVisible ? "var(--banner-height-active)" : "0px"
    );
  }, [bannerVisible]);

  useEffect(() => () => {
    document.documentElement.style.setProperty("--banner-height", "0px");
  }, []);

  return (
    <div className="App" data-testid="landing-root">
      <OfferBanner visible={bannerVisible} onDismiss={() => setBannerVisible(false)}/>
      <Navbar/>
      <Hero/>
      <About/>
      <ClinicGallery/>
      <Conditions/>
      <YourFirstVisit/>
      <Pricing/>
      <WhyUs/>
      <Process/>
      <Testimonials/>
      <Contact/>
      <FAQ/>
      <AreasWeServe/>
      <Footer/>
      <FloatingButtons/>
      <StickyMobileBar/>
    </div>
  );
}
